/**
 * BubbleTails Component
 *
 * Y2K Phase 3 (P3): Multiple bubble tails for group dynamics transformations.
 * Extracted from TransformationInsightCard.tsx (Line Count Refactor 2025-11-21)
 */

import { useEffect } from 'react';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  Easing,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';

/**
 * Y2K Phase 3 (P3): Speech Bubble Tail Component
 *
 * Renders a single SVG triangle tail pointing in specified direction.
 * Used to create "multiple people speaking" effect for group dynamics transformations.
 *
 * Animation:
 * - Duration: 500ms fade-in
 * - Staggered delays: 0ms, 500ms, 1000ms (left, right, bottom)
 * - Easing: `Easing.out(Easing.ease)`
 *
 * Visual Specification:
 * - Size: 20x20px SVG viewBox
 * - Color: #C8A882 (Brushed Gold, Layer 3 accent)
 * - Absolute positioning outside card boundaries
 *
 * Accessibility:
 * - `accessibilityElementsHidden={true}` (decorative SVG)
 * - No animation when reduced motion enabled (opacity: 1 immediately)
 *
 * @param direction - Tail direction ('left' | 'right' | 'bottom')
 * @param delay - Stagger delay in milliseconds (0ms, 500ms, 1000ms)
 * @param isReducedMotion - True when user has reduced motion enabled
 *
 * @example
 * <BubbleTail direction="left" delay={0} isReducedMotion={false} />
 * <BubbleTail direction="right" delay={500} isReducedMotion={false} />
 * <BubbleTail direction="bottom" delay={1000} isReducedMotion={false} />
 */
function BubbleTail({
  direction,
  delay,
  isReducedMotion,
}: {
  direction: 'left' | 'right' | 'bottom';
  delay: number;
  isReducedMotion: boolean;
}) {
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (!isReducedMotion) {
      opacity.value = withDelay(
        delay,
        withTiming(1, { duration: 500, easing: Easing.out(Easing.ease) })
      );
    } else {
      opacity.value = 1;
    }
  }, [delay, isReducedMotion]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  // Tail positioning and path based on direction
  const tailConfig = {
    left: {
      style: { position: 'absolute' as const, left: -12, top: 30 },
      path: 'M 12 0 L 0 10 L 12 20 Z',
    },
    right: {
      style: { position: 'absolute' as const, right: -12, top: 50 },
      path: 'M 0 0 L 12 10 L 0 20 Z',
    },
    bottom: {
      style: { position: 'absolute' as const, bottom: -15 },
      path: 'M 0 0 L 10 15 L 20 0 Z',
    },
  };

  const config = tailConfig[direction];

  return (
    <Animated.View
      style={[
        config.style,
        animatedStyle,
        // Bottom tail needs left positioning as percentage (not supported in static style object)
        direction === 'bottom' && { left: '40%' },
      ]}
      accessibilityElementsHidden={true}
    >
      <Svg width="20" height="20" viewBox="0 0 20 20">
        <Path d={config.path} fill="#C8A882" />
      </Svg>
    </Animated.View>
  );
}

/**
 * Y2K Phase 3 (P3): Multiple Bubble Tails Container
 *
 * Renders 3 bubble tails (left, right, bottom) with sequential fade-in animation.
 * Creates "multiple people speaking" visual effect for group dynamics transformations.
 *
 * Trigger Conditions:
 * 1. Transformation is group dynamics type (`isGroupDynamicsTransformation()` returns true)
 * 2. User is Layer 3 (DramaX subscriber) - enforced by parent component
 *
 * Animation Sequence:
 * - Left tail: 0ms delay
 * - Right tail: 500ms delay
 * - Bottom tail: 1000ms delay
 *
 * @param isReducedMotion - True when user has reduced motion enabled
 *
 * @example
 * // In TransformationInsightCard component:
 * const showBubbleTails = isGroupDynamicsTransformation(transformationType) && tierLevel === 3;
 * {showBubbleTails && <MultipleBubbleTails isReducedMotion={isReducedMotion} />}
 */
export function MultipleBubbleTails({ isReducedMotion }: { isReducedMotion: boolean }) {
  return (
    <>
      <BubbleTail direction="left" delay={0} isReducedMotion={isReducedMotion} />
      <BubbleTail direction="right" delay={500} isReducedMotion={isReducedMotion} />
      <BubbleTail direction="bottom" delay={1000} isReducedMotion={isReducedMotion} />
    </>
  );
}
